import { useState } from 'react';
import { C } from '../../theme/palette';
import { initiales, PASTILLES } from './listes';
import { alerte, bouton, carte } from './styles';

/**
 * « 🏬 Mes fournisseurs » : chez qui commander. Chaque magasin qui a invité
 * la personne a sa carte ; « Commander » ouvre son espace. Ses propres
 * entreprises restent à portée, sous « Mon entreprise ».
 *
 * @param {object} props propriétés
 * @param {object[]} props.fournisseurs magasins dont la personne est cliente
 * @param {object[]} props.entreprises ses propres sociétés
 * @param {string} [props.entete] son entreprise et son nom
 * @param {(id: number) => Promise<any>} props.basculeSociete ouvre une autre société
 * @param {() => void} props.recharge recharge la page, dans la société ouverte
 * @param {() => void} props.commander revient au catalogue du magasin ouvert
 * @returns {import('react').ReactElement} la liste
 */
export function MesFournisseurs({
  fournisseurs,
  entreprises,
  entete,
  basculeSociete,
  recharge,
  commander,
}) {
  const [enCours, setEnCours] = useState(null);
  const [erreur, setErreur] = useState('');
  const ouvre = async (s) => {
    if (s.courante) {
      commander();
      return;
    }
    setEnCours(s.id);
    setErreur('');
    try {
      await basculeSociete(s.id);
      // Chaque magasin a son catalogue, ses prix, ses commandes : la page
      // repart de zéro, sans rien garder de celui qu'on quitte.
      recharge();
    } catch (e) {
      setErreur(e?.message || 'Ce magasin n’a pas pu s’ouvrir. Réessayez.');
      setEnCours(null);
    }
  };
  return (
    <section aria-labelledby="titre-fournisseurs">
      <h2 id="titre-fournisseurs" style={{ fontSize: 18, margin: '4px 0 2px' }}>
        🏬 Mes fournisseurs
      </h2>
      {entete && <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>{entete}</div>}
      <p style={{ fontSize: 13, color: C.grey, margin: '0 0 10px' }}>
        Chez qui voulez-vous commander ?
      </p>
      {fournisseurs.map((s, i) => {
        const [fond, encre] = PASTILLES[i % PASTILLES.length];
        return (
          <div
            key={s.id}
            style={{
              ...carte,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              border: '1.5px solid ' + (s.courante ? C.teal : 'transparent'),
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 46,
                height: 46,
                borderRadius: 10,
                background: fond,
                color: encre,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 900,
                flexShrink: 0,
              }}
            >
              {initiales(s.nom)}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <b style={{ display: 'block', fontSize: 15 }}>{s.nom}</b>
              {s.courante && (
                <span style={{ fontSize: 12, color: C.teal, fontWeight: 700 }}>● Ouvert</span>
              )}
            </span>
            <button
              onClick={() => ouvre(s)}
              disabled={enCours != null}
              aria-label={'Commander chez ' + s.nom}
              style={{ ...bouton(C.green), padding: '10px 12px', fontSize: 13 }}
            >
              {enCours === s.id ? '…' : 'Commander'}
            </button>
          </div>
        );
      })}
      <div
        style={{
          textAlign: 'center',
          border: '2px dashed #9CA3AF',
          color: C.grey,
          borderRadius: 14,
          padding: 12,
          fontSize: 13,
          lineHeight: 1.5,
          marginBottom: 10,
        }}
      >
        Un nouveau fournisseur apparaît ici dès que vous acceptez son invitation.
      </div>
      {entreprises.length > 0 && (
        <>
          <h3 style={{ fontSize: 15, margin: '14px 0 8px' }}>🏢 Mon entreprise</h3>
          {entreprises.map((s) => (
            <div key={s.id} style={{ ...carte, display: 'flex', alignItems: 'center', gap: 12 }}>
              <b style={{ flex: 1, minWidth: 0, fontSize: 15 }}>{s.nom}</b>
              <button
                onClick={() => ouvre(s)}
                disabled={enCours != null}
                aria-label={'Ouvrir ' + s.nom}
                style={{ ...bouton(C.navy, false), padding: '10px 12px', fontSize: 13 }}
              >
                {enCours === s.id ? '…' : 'Ouvrir'}
              </button>
            </div>
          ))}
        </>
      )}
      {erreur && (
        <div role="alert" style={alerte}>
          {erreur}
        </div>
      )}
      <div
        style={{
          background: '#E8F3EC',
          borderRadius: 12,
          padding: '10px 12px',
          fontSize: 12,
          color: '#14532D',
          lineHeight: 1.5,
          marginTop: 10,
        }}
      >
        Un seul compte, un seul mot de passe. Chaque magasin ne voit que ses commandes et vos prix
        chez lui, jamais ceux des autres.
      </div>
    </section>
  );
}
